package com.islamicmedia.shared.domain.model

/** Inline span within a paragraph or list item. */
sealed class TextSpan {
    data class Normal(val text: String) : TextSpan()
    data class Bold(val text: String) : TextSpan()
    data class Italic(val text: String) : TextSpan()
}

/** A block-level element produced by the markdown parser. */
sealed class TextBlock {
    data class Heading(val text: String) : TextBlock()
    data class Paragraph(val spans: List<TextSpan>) : TextBlock()
    data class Table(val headers: List<String>, val rows: List<List<String>>) : TextBlock()
    data class NumberedList(val items: List<List<TextSpan>>) : TextBlock()
    data class BulletList(val items: List<List<TextSpan>>) : TextBlock()
}

/**
 * Lightweight markdown parser that handles:
 * - ## Headings and **BOLD HEADINGS**
 * - **bold** inline text
 * - Numbered lists (1. item)
 * - Bullet lists (- item, • item, * item)
 * - Plain paragraphs (separated by blank lines)
 */
fun parseMarkdownToBlocks(text: String): List<TextBlock> {
    if (text.isBlank()) return emptyList()

    // Pre-process: filter profanity, then normalize formatting
    val filtered = filterProfanity(text)
    val normalized = normalizeMarkdown(filtered)
    val lines = normalized.lines()
    val blocks = mutableListOf<TextBlock>()
    var i = 0

    while (i < lines.size) {
        val line = lines[i].trim()

        // Skip blank lines
        if (line.isEmpty()) {
            i++
            continue
        }

        // Markdown heading: "## Heading" or "### Heading"
        val headingMatch = Regex("^#{1,4}\\s+(.+)").matchEntire(line)
        if (headingMatch != null) {
            val headingText = headingMatch.groupValues[1]
                .replace(Regex("\\*\\*(.+?)\\*\\*"), "$1") // strip bold markers from headings
                .trim()
            blocks.add(TextBlock.Heading(headingText))
            i++
            continue
        }

        // Bold-only line that looks like a section header (e.g., **MAGIC AND THE OCCULT**)
        val boldHeaderMatch = Regex("^\\*\\*([^*]+)\\*\\*\\s*$").matchEntire(line)
        if (boldHeaderMatch != null) {
            blocks.add(TextBlock.Heading(boldHeaderMatch.groupValues[1].trim()))
            i++
            continue
        }

        // Markdown table: lines starting with |
        if (line.startsWith("|") && line.endsWith("|")) {
            val tableLines = mutableListOf<String>()
            while (i < lines.size) {
                val l = lines[i].trim()
                if (l.startsWith("|") && l.endsWith("|")) {
                    tableLines.add(l)
                    i++
                } else {
                    break
                }
            }
            if (tableLines.size >= 2) {
                val parsedRows = tableLines.map { row ->
                    row.trim('|').split("|").map { it.trim() }
                }
                // Filter out separator rows (|---|---|)
                val dataRows = parsedRows.filter { row ->
                    !row.all { cell -> cell.matches(Regex("^[-:]+$")) }
                }
                if (dataRows.isNotEmpty()) {
                    val headers = dataRows.first()
                    val bodyRows = dataRows.drop(1)
                    blocks.add(TextBlock.Table(headers = headers, rows = bodyRows))
                }
            }
            continue
        }

        // Numbered list: "1. ", "2. ", etc. — skip blank lines between items
        if (line.matches(Regex("^\\d+\\.\\s+.*"))) {
            val items = mutableListOf<List<TextSpan>>()
            while (i < lines.size) {
                val l = lines[i].trim()
                if (l.isEmpty()) {
                    // Peek ahead: if next non-blank line is a numbered item, skip the blank line
                    val nextNonBlank = lines.drop(i + 1).firstOrNull { it.trim().isNotEmpty() }
                    if (nextNonBlank != null && nextNonBlank.trim().matches(Regex("^\\d+\\.\\s+.*"))) {
                        i++
                        continue
                    } else {
                        break
                    }
                }
                val match = Regex("^\\d+\\.\\s+(.*)").matchEntire(l)
                if (match != null) {
                    items.add(parseInlineSpans(match.groupValues[1]))
                    i++
                } else {
                    break
                }
            }
            blocks.add(TextBlock.NumberedList(items))
            continue
        }

        // Bullet list: "- ", "• ", "* " (but not "**bold**" which starts with *)
        if (line.matches(Regex("^[-•]\\s+.*")) ||
            (line.startsWith("* ") && !line.startsWith("**"))) {
            val items = mutableListOf<List<TextSpan>>()
            val bulletPattern = Regex("^[-•*]\\s+(.*)")
            while (i < lines.size) {
                val l = lines[i].trim()
                if (l.isEmpty()) {
                    // Peek ahead: if next non-blank line is a bullet, skip the blank line
                    val nextNonBlank = lines.drop(i + 1).firstOrNull { it.trim().isNotEmpty() }
                    if (nextNonBlank != null && (nextNonBlank.trim().matches(Regex("^[-•]\\s+.*")) ||
                                (nextNonBlank.trim().startsWith("* ") && !nextNonBlank.trim().startsWith("**")))) {
                        i++
                        continue
                    } else {
                        break
                    }
                }
                val match = bulletPattern.matchEntire(l)
                if (match != null && !(l.startsWith("**") && !l.startsWith("* "))) {
                    items.add(parseInlineSpans(match.groupValues[1]))
                    i++
                } else {
                    break
                }
            }
            blocks.add(TextBlock.BulletList(items))
            continue
        }

        // Regular paragraph — collect consecutive non-blank, non-special lines
        val paragraphLines = mutableListOf<String>()
        while (i < lines.size) {
            val l = lines[i].trim()
            if (l.isEmpty() ||
                l.matches(Regex("^#{1,4}\\s+.*")) ||
                l.matches(Regex("^\\*\\*[^*]+\\*\\*\\s*$")) ||
                l.matches(Regex("^\\d+\\.\\s+.*")) ||
                l.matches(Regex("^[-•]\\s+.*")) ||
                (l.startsWith("* ") && !l.startsWith("**"))
            ) break
            paragraphLines.add(l)
            i++
        }
        val combined = paragraphLines.joinToString(" ")
        blocks.add(TextBlock.Paragraph(parseInlineSpans(combined)))
    }

    return blocks
}

/** Profanity filter — replaces curse words with censored versions. Case-insensitive. */
private val profanityMap = listOf(
    "fuck" to "f--k",
    "shit" to "s--t",
    "bitch" to "b---h",
    "ass" to "a-s",
    "asshole" to "a--hole",
    "damn" to "d--n",
    "dick" to "d--k",
    "cock" to "c--k",
    "pussy" to "p---y",
    "cunt" to "c--t",
    "bastard" to "b----rd",
    "whore" to "w---e",
    "slut" to "s--t",
    "nigger" to "n----r",
    "nigga" to "n---a",
    "faggot" to "f----t",
    "motherfucker" to "m-----f----r",
    "bullshit" to "b------t",
)

fun filterProfanity(text: String): String {
    var result = text
    // Process longer words first to avoid partial replacements (e.g. "motherfucker" before "fuck")
    val sorted = profanityMap.sortedByDescending { it.first.length }
    for ((word, replacement) in sorted) {
        try {
            // Use word boundaries (\b) to avoid matching "passing" for "ass", "class" for "ass", etc.
            // Use RegexOption.IGNORE_CASE instead of (?i) which isn't supported in JS
            val pattern = Regex("\\b${Regex.escape(word)}\\b", RegexOption.IGNORE_CASE)
            result = result.replace(pattern) { match ->
                // Preserve original casing of first letter
                if (match.value[0].isUpperCase()) {
                    replacement.replaceFirstChar { it.uppercase() }
                } else {
                    replacement
                }
            }
        } catch (_: Exception) {
            // If regex fails on any platform, skip this word silently
        }
    }
    return result
}

/**
 * Normalize common LLM formatting quirks:
 * - Split inline numbered sections like "**1. HEADING**" onto their own lines
 * - Split "**N.\nHEADING**" patterns (number + heading split across bold)
 */
private fun normalizeMarkdown(text: String): String {
    var result = text

    // Pattern: "**1. MAGIC AND THE OCCULT (Subtitle)**" → heading on its own line
    // Also handles "**1.\nMAGIC AND THE OCCULT**" across lines
    result = result.replace(
        Regex("\\*\\*\\s*\\d+\\.\\s*\\n?\\s*([^*]+?)\\*\\*"),
        "\n## $1\n"
    )

    // Pattern: standalone "**SECTION HEADER**" already handled by parser,
    // but ensure they're on their own line
    result = result.replace(Regex("([^\n])\\s*\\*\\*([A-Z][A-Z\\s,()'-]{5,})\\*\\*")) { match ->
        "${match.groupValues[1]}\n\n**${match.groupValues[2]}**\n"
    }

    // Remove horizontal rules (---, ___, ***)
    result = result.replace(Regex("^\\s*(-{3,}|_{3,}|\\*{3,})\\s*$", RegexOption.MULTILINE), "")

    // Tables are parsed directly in parseMarkdownToBlocks, no normalization needed

    return result
}

/** Parse inline **bold** and *italic* spans within a line of text. */
fun parseInlineSpans(text: String): List<TextSpan> {
    if (text.isBlank()) return listOf(TextSpan.Normal(text))

    val spans = mutableListOf<TextSpan>()
    // Match **bold** first, then *italic* (order matters — bold has longer delimiter)
    val regex = Regex("\\*\\*(.+?)\\*\\*|\\*(.+?)\\*")
    var lastIndex = 0

    for (match in regex.findAll(text)) {
        // Text before the match
        if (match.range.first > lastIndex) {
            spans.add(TextSpan.Normal(text.substring(lastIndex, match.range.first)))
        }
        if (match.groupValues[1].isNotEmpty()) {
            // **bold**
            spans.add(TextSpan.Bold(match.groupValues[1]))
        } else {
            // *italic*
            spans.add(TextSpan.Italic(match.groupValues[2]))
        }
        lastIndex = match.range.last + 1
    }

    // Remaining text
    if (lastIndex < text.length) {
        spans.add(TextSpan.Normal(text.substring(lastIndex)))
    }

    return spans.ifEmpty { listOf(TextSpan.Normal(text)) }
}
